import { getErrorMessage } from "@/core/api/errors";
import { dayjs, formatDateAndTime, formatDateRange } from "@/core/utils/date";
import { MAX_REPORT_SPAN_DAYS, RUN_STATUS } from "./constants";
import { DEFAULT_CURRENCY, formatMoney, withTrueMinus } from "./currency";
import { isNumericType, MISSING, missingNote } from "./missing";
import type {
  EnrichedProfitabilityPeriod,
  EnrichedProfitabilityRun,
  ProfitabilityPeriod,
  ProfitabilityRun,
  ReportDates,
  RunSummaryFigure,
} from "./types";

export const isActiveRun = (run: ProfitabilityRun) =>
  run.status === RUN_STATUS.pending || run.status === RUN_STATUS.running;

export const runDates = (run: {
  start_date: string | null;
  end_date: string | null;
}): ReportDates | null =>
  run.start_date && run.end_date
    ? { start_date: run.start_date, end_date: run.end_date }
    : null;

export const documentsLabel = (documents: ProfitabilityRun["documents"]) => {
  const names = (documents ?? []).map((document) => document.file_name);
  if (names.length === 0) return "Uploaded documents";
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
};

const isIsoDate = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  dayjs(value, "YYYY-MM-DD", true).isValid();

export const toIsoDate = (date: Date | null) =>
  date ? dayjs(date).format("YYYY-MM-DD") : "";

/** Days a range covers, counting both ends, the way the API counts them. */
const spanInDays = ({ start_date, end_date }: ReportDates) =>
  dayjs(end_date).diff(dayjs(start_date), "day") + 1;

export const dateRangeError = (dates: ReportDates) => {
  const { start_date, end_date } = dates;
  if (!isIsoDate(start_date) || !isIsoDate(end_date))
    return "Enter a start and end date.";
  if (start_date > end_date) return "The start date has to come first.";
  // Checked here as well as on the server, so a range that cannot work is said
  // so before the request rather than after it.
  const span = spanInDays(dates);
  if (span > MAX_REPORT_SPAN_DAYS)
    return `A report can cover at most ${MAX_REPORT_SPAN_DAYS} days. This range is ${span}.`;
  return null;
};

export const formatReportPeriod = ({ start_date, end_date }: ReportDates) =>
  formatDateRange(start_date, end_date);

export const formatReportRange = (start: string, end: string) =>
  formatDateRange(start, end);

/** "30 days" — the span an owner asked for, not the time it took to build. */
export const formatReportSpan = (dates: ReportDates) => {
  const days = spanInDays(dates);
  return days === 1 ? "1 day" : `${days} days`;
};

/**
 * A headline figure the report's script tagged, formatted by the type it
 * declared. The script chooses which figures exist, so this must not assume any
 * particular one.
 */
export const formatSummaryValue = (
  { value, type }: Pick<RunSummaryFigure, "value" | "type">,
  /** The report's currency (`useReportCurrency`); USD when it declared none. */
  currency: string = DEFAULT_CURRENCY,
) => {
  // A figure with no value is missing, not zero and not blank (FIN-004).
  if (value === null && isNumericType(type)) return MISSING;
  if (typeof value !== "number") return String(value ?? "");
  if (type === "percent")
    return `${withTrueMinus(value * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  if (type === "currency") return formatMoney(value, currency);
  return value.toLocaleString();
};

export const reportError = (error: unknown) => getErrorMessage(error);

export const reportPath = (businessId: string | null, runId?: string) =>
  `/businesses/${businessId}/profitability_report_runs${runId ? `/${runId}` : ""}`;

export const workbookPath = (businessId: string, runId: string) =>
  `${reportPath(businessId, runId)}/workbook`;

/**
 * Chat root for one report, in the shape the shared explanation-chat transport
 * expects (POST here, plus `/threads` and `/stream`).
 */
export const reportChatPath = (businessId: string, runId: string) =>
  `${reportPath(businessId, runId)}/chat`;

export const downloadFilename = (run: ProfitabilityRun) =>
  run.start_date && run.end_date
    ? `profitability-${run.start_date}-${run.end_date}.xlsx`
    : `profitability-${dayjs(run.created_at).format("YYYY-MM-DD")}.xlsx`;

/** Preserve the API's figure labels and order; add only display formatting. */
export const enrichProfitabilityRun = (
  run: ProfitabilityRun,
): EnrichedProfitabilityRun => {
  const dates = runDates(run);
  const span = dates ? formatReportSpan(dates) : "";
  const requestedAt = formatDateAndTime(run.created_at);
  const summary = run.summary.map((figure) => ({
    ...figure,
    // In the report's own currency, which the run list has no provider for.
    displayValue: formatSummaryValue(figure, run.currency ?? undefined),
    // The report's own label, as written: guessing case breaks labels like "GM %".
    inlineLabel: figure.label,
  }));
  return {
    ...run,
    display: {
      // A draft's sample covers the files it was built from, not a period.
      period: dates
        ? formatReportPeriod(dates)
        : run.draft_sample
          ? "Sample run"
          : documentsLabel(run.documents),
      span,
      requestedAt,
      completedAt:
        run.status === RUN_STATUS.completed && run.completed_at
          ? formatDateAndTime(run.completed_at)
          : null,
      metadata: [
        `Requested by ${run.requester.full_name}`,
        requestedAt,
        span,
      ].filter(Boolean),
      workbookReady:
        run.status === RUN_STATUS.completed && run.workbook_available,
      summary,
      supportingFigures: summary.slice(1).map((figure) => {
        const note = missingNote(figure.missing);
        return `${figure.displayValue} ${figure.inlineLabel}${note ? ` (${note})` : ""}`;
      }),
    },
  };
};

/** Keep the available report visible while describing a newer attempt. */
export const enrichProfitabilityPeriod = (
  period: ProfitabilityPeriod,
): EnrichedProfitabilityPeriod => {
  const run = enrichProfitabilityRun(period.report ?? period.latest_attempt);
  const attempt = period.latest_attempt;
  let latestAttemptMessage: string | null = null;
  if (attempt.id !== run.id) {
    switch (attempt.status) {
      case RUN_STATUS.failed:
        latestAttemptMessage = `Latest attempt failed. ${attempt.error?.message ?? "Try again."}`;
        break;
      case RUN_STATUS.pending:
        latestAttemptMessage = "New report queued";
        break;
      case RUN_STATUS.running:
        latestAttemptMessage = "New report running";
        break;
      case RUN_STATUS.completed:
        // A newer result held back because this one was reviewed or chosen
        // says so in the row, beside the way to use it (TIME-006).
        latestAttemptMessage =
          period.correction?.id === attempt.id
            ? null
            : "Latest attempt has no workbook";
        break;
    }
  }
  return {
    ...run,
    correction: period.correction ?? null,
    display: { ...run.display, latestAttemptMessage },
  };
};
